import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import IconButton from "../../common/Button";
import SearchBox from "../../common/SearchBox";
import { CheckIcon } from "../../common/Icons";
import Spacer from "../../common/Spacer";
import ItemThumbnail from "../../common/ItemThumbnail";

const AddItemOverlay = ({
  allItems,
  selectedItemIds,
  onClose,
  onUpdateSelectedItems,
  addButtonText = "Add to section",
}) => {
  // State for tracking selected item IDs in the overlay
  const [overlaySelectedItemIds, setOverlaySelectedItemIds] = useState([
    ...selectedItemIds,
  ]);
  const [filteredItems, setFilteredItems] = useState(allItems);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    setOverlaySelectedItemIds(selectedItemIds);
  }, [selectedItemIds]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle checkbox change for adding/removing items
  const handleCheckboxChange = (itemId) => {
    setOverlaySelectedItemIds((prevSelectedIds) =>
      prevSelectedIds.includes(itemId)
        ? prevSelectedIds.filter((id) => id !== itemId)
        : [...prevSelectedIds, itemId]
    );
  };

  // Handle updating the selected items in the parent component
  const handleUpdateItems = () => {
    onUpdateSelectedItems(overlaySelectedItemIds);
    onClose();
  };

  const handleSearch = (results) => {
    setFilteredItems(results);
  };

  const availableItems = useMemo(
    () =>
      filteredItems
        .filter((item) => !item.hide_from_service_user_specific)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title)),
    [filteredItems]
  );

  return (
    <FullScreenOverlay
      title="Add Items"
      iconClose={true}
      onClose={onClose}
      width={isMobile ? "100%" : "60%"}
      height="100%"
      alignment={"right"}
      buttons={[]}
    >
      <FormSection>
        <p className="body-text-regular regular neutral-900">
          Select and add items into your Care Plan. You can edit the description
          to match your patient's need.
        </p>
        <Spacer height="16px" />

        <SearchBox
          placeholder="Search items"
          onSearch={handleSearch}
          searchFields={["title", "description"]}
          data={allItems}
        />
        <Spacer height="16px" />

        {/* Items List with Checkboxes */}
        <ItemsContainer>
          <ItemsList>
            {availableItems.map((item) => {
              const isDisabled = selectedItemIds.includes(item.id); // Item is disabled if already selected
              return (
                <ItemRow
                  key={item.id}
                  selected={overlaySelectedItemIds.includes(item.id)}
                  disabled={isDisabled} // Disable the row if the item is already selected
                  onClick={() => !isDisabled && handleCheckboxChange(item.id)} // Only allow selection if not disabled
                >
                  <ItemThumbnail
                    disabled={isDisabled}
                    src={item?.image_url}
                    alt={item?.image_alt_text || item?.title}
                    width={"180px"}
                    height={"164px"}
                    borderRadiusTopLeft="12px"
                    borderRadiusBottomLeft="12px"
                  />

                  <TextContent>
                    <ItemDetails>
                      <ItemTitle disabled={isDisabled}>{item.title}</ItemTitle>
                      <ItemDescription disabled={isDisabled}>
                        {item.description}
                      </ItemDescription>
                    </ItemDetails>
                    {!isDisabled && ( // Only show checkbox if the item is not disabled
                      <CheckboxFaker
                        checked={overlaySelectedItemIds.includes(item.id)}
                      >
                        <span className="icon">
                          {overlaySelectedItemIds.includes(item.id) && (
                            <CheckIcon color="#FFF" />
                          )}
                        </span>
                      </CheckboxFaker>
                    )}
                  </TextContent>
                </ItemRow>
              );
            })}
          </ItemsList>
        </ItemsContainer>

        <FloatingButton>
          <IconButton
            variant="primary"
            state="idle"
            width={"100%"}
            onClick={handleUpdateItems}
          >
            {addButtonText}
          </IconButton>
        </FloatingButton>
      </FormSection>
    </FullScreenOverlay>
  );
};

// Styled components

const TextContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  height: 164px;
  padding: 16px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0px 40px;

  @media (max-width: 768px) {
    padding: 0px 16px;
  }
`;

const ItemsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 2px solid
    ${({ selected, disabled }) =>
      disabled ? "#CED4DA" : selected ? "#2399a1" : "#CED4DA"};
  margin-bottom: 24px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  background-color: ${({ disabled }) => (disabled ? "#F8F9FA" : "transparent")};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  gap: 8px;
`;

const ItemTitle = styled.h6`
  margin: 0;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ disabled }) => (disabled ? "#6C757D" : "inherit")};
`;

const ItemDescription = styled.p`
  margin: 4px 0;
  color: ${({ disabled }) => (disabled ? "#6C757D" : "#555")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const CheckboxFaker = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.checked ? "#2399A1" : "#6c757d")};
  margin-left: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: ${(props) => (props.checked ? "#2399A1" : "transparent")};
  transition: background-color 0.2s, border-color 0.2s;

  .icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: ${(props) => (props.checked ? "scale(1)" : "scale(0)")};
    transition: transform 0.3s ease;
  }
`;

const FloatingButton = styled.div`
  position: sticky;
  bottom: 0;
  background-color: white;
  padding-top: 16px;
  padding-bottom: 16px;
`;

export default AddItemOverlay;
